'use client';

import React from 'react';
import Link from 'next/link';
import { ArrowLeft, Clock, Calendar } from 'lucide-react';

export default function CompoundingLowTurnoverPage() {
  return (
    <div
      className="min-h-screen"
      style={{ backgroundColor: 'var(--background)', color: 'var(--foreground)' }}
    >
      <header
        className="sticky top-0 z-50 flex items-center justify-between px-6 md:px-12 lg:px-20"
        style={{
          height: '72px',
          backgroundColor: 'rgba(13,26,58,0.95)',
          borderBottom: '1px solid rgba(30,48,104,0.6)',
          backdropFilter: 'blur(12px)',
        }}
      >
        <Link
          href="/#insights"
          className="flex items-center gap-2 text-sm font-medium"
          style={{ color: 'var(--muted-foreground)' }}
        >
          <ArrowLeft size={15} />
          Back to Perspectives
        </Link>
        <div className="flex items-center gap-2">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img src="/assets/macro-capital-logo.svg" alt="Macro Capital" style={{ width: '36px', height: '36px' }} />
          <span className="text-sm font-bold tracking-widest uppercase" style={{ color: 'var(--foreground)' }}>
            Macro Capital
          </span>
        </div>
      </header>

      <article className="max-w-3xl mx-auto px-6 py-16">
        <div className="flex flex-wrap items-center gap-4 mb-6">
          <span
            className="text-[10px] font-semibold tracking-[0.18em] uppercase px-3 py-1 rounded-full"
            style={{
              backgroundColor: 'rgba(34,197,94,0.08)',
              border: '1px solid rgba(34,197,94,0.2)',
              color: '#4ade80',
            }}
          >
            Strategy
          </span>
          <span className="flex items-center gap-1.5 text-xs" style={{ color: 'var(--muted-foreground)' }}>
            <Calendar size={11} /> February 2025
          </span>
          <span className="flex items-center gap-1.5 text-xs" style={{ color: 'var(--muted-foreground)' }}>
            <Clock size={11} /> 5 min read
          </span>
        </div>

        <h1
          className="font-semibold leading-tight mb-6"
          style={{
            fontFamily: 'var(--font-serif)',
            fontSize: 'clamp(1.8rem, 4vw, 2.8rem)',
            color: 'var(--foreground)',
          }}
        >
          The Compounding Advantage of Low Turnover
        </h1>

        <p className="text-lg leading-relaxed mb-10" style={{ color: 'var(--muted-foreground)' }}>
          Transaction costs, taxes, and behavioural drag erode returns silently. A low-turnover approach compounds these savings over time — and the mathematics are more powerful than most investors appreciate.
        </p>

        <div className="h-px mb-10" style={{ backgroundColor: 'var(--border)' }} />

        <div className="space-y-8" style={{ color: 'var(--muted-foreground)', lineHeight: '1.85' }}>

          <section>
            <h2 className="text-xl font-semibold mb-4" style={{ fontFamily: 'var(--font-serif)', color: 'var(--foreground)' }}>
              The Hidden Cost of Activity
            </h2>
            <p className="mb-4">
              The investment industry has a structural bias toward activity. Fund managers are paid to make decisions. Analysts are rewarded for generating new ideas. Trading desks profit from volume. The entire ecosystem is oriented toward doing something — buying, selling, rotating, rebalancing. Yet the evidence consistently shows that activity, in aggregate, destroys value rather than creating it.
            </p>
            <p className="mb-4">
              The costs of high turnover are multiple and compounding. The most visible are transaction costs — brokerage commissions, bid-ask spreads, and market impact. For a large institutional portfolio, these can amount to 0.5–1.0% of assets per year for a high-turnover strategy. Less visible but equally significant are the tax costs: in taxable accounts, realising gains triggers a tax liability that reduces the capital available for reinvestment. And least visible of all is the behavioural drag — the tendency of active traders to buy high and sell low, driven by emotion rather than analysis.
            </p>
            <p>
              When these costs are aggregated and compounded over a decade or more, the mathematics become stark. A portfolio that incurs 1.5% per year in total turnover costs — not an unreasonable estimate for an actively traded fund — will underperform a low-turnover equivalent by approximately 16% over ten years, assuming identical gross returns. Over twenty years, the gap widens to nearly 35%.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-4" style={{ fontFamily: 'var(--font-serif)', color: 'var(--foreground)' }}>
              The Mathematics of Compounding Savings
            </h2>
            <p className="mb-4">
              Consider two portfolios, each earning 10% gross returns per year. Portfolio A has annual turnover of 100% and incurs total costs of 1.5% per year, yielding a net return of 8.5%. Portfolio B has annual turnover of 20% and incurs total costs of 0.3% per year, yielding a net return of 9.7%.
            </p>
            <p className="mb-4">
              Over ten years, a ₹1 crore investment in Portfolio A grows to ₹2.26 crore. The same investment in Portfolio B grows to ₹2.52 crore — a difference of ₹26 lakhs, or 26% more wealth, from the same gross return. Over twenty years, the gap widens dramatically: Portfolio A grows to ₹5.11 crore, while Portfolio B grows to ₹6.35 crore — a difference of ₹1.24 crore, or 24% more wealth.
            </p>
            <p>
              These numbers illustrate a fundamental truth about investing: the return you earn matters less than the return you keep. And the return you keep is determined not just by your investment skill, but by the costs you incur in exercising that skill.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-4" style={{ fontFamily: 'var(--font-serif)', color: 'var(--foreground)' }}>
              Behavioural Drag: The Invisible Destroyer
            </h2>
            <p className="mb-4">
              Of all the costs of high turnover, behavioural drag is the most insidious because it is the least measurable. It manifests in the gap between the returns that a fund earns and the returns that its investors actually receive — a gap that arises because investors tend to add money after periods of strong performance and withdraw it after periods of weak performance.
            </p>
            <p className="mb-4">
              DALBAR's annual Quantitative Analysis of Investor Behavior consistently shows that the average equity fund investor earns significantly less than the average equity fund, because of poorly timed entry and exit decisions. Over the twenty years to 2023, the average equity fund investor earned approximately 6.3% per year, compared to 9.7% for the S&P 500 — a gap of 3.4 percentage points per year, almost entirely attributable to behavioural timing errors.
            </p>
            <p>
              A low-turnover strategy, by its nature, discourages this kind of behaviour. When a fund manager commits to holding positions for three to five years, it signals a long-term orientation that attracts like-minded investors. The result is a more stable investor base, lower redemption pressure during downturns, and a virtuous cycle that allows the manager to maintain conviction through periods of short-term underperformance.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-4" style={{ fontFamily: 'var(--font-serif)', color: 'var(--foreground)' }}>
              Our Approach
            </h2>
            <p className="mb-4">
              At Macro Capital, our target portfolio turnover is below 25% per year. This means we expect to hold the average position for four or more years. This is not a constraint imposed on us — it is a deliberate choice that reflects our conviction that the compounding advantage of low turnover is one of the most reliable edges available to long-term investors.
            </p>
            <p className="mb-4">
              The discipline required to maintain low turnover is considerable. Markets are noisy, and there is always a compelling reason to trade — a new macro development, a change in sector dynamics, a valuation anomaly that seems too good to ignore. Resisting these temptations requires a clear investment philosophy, a rigorous analytical framework, and the psychological fortitude to hold through periods of short-term underperformance.
            </p>
            <p>
              We believe that this discipline, applied consistently over time, is one of the most powerful sources of alpha available to investors. Not because we are smarter than the market — we are not — but because we are more patient. And in investing, patience is a form of intelligence that compounds just as powerfully as capital.
            </p>
          </section>

        </div>

        <div className="mt-16 pt-8" style={{ borderTop: '1px solid var(--border)' }}>
          <p className="text-xs mb-6" style={{ color: 'var(--muted-foreground)' }}>
            This article is for informational purposes only and does not constitute investment advice. Past performance is not indicative of future results. For qualified investors only.
          </p>
          <Link
            href="/#insights"
            className="inline-flex items-center gap-2 text-sm font-semibold"
            style={{ color: 'var(--primary)' }}
          >
            <ArrowLeft size={14} />
            Back to all perspectives
          </Link>
        </div>
      </article>
    </div>
  );
}
